<template>
  <div
    class="group relative h-full flex flex-col rounded-2xl overflow-hidden border-2 border-[#3A5A7A] shadow-lg hover:shadow-xl transition-all duration-300 hover:border-[#5A6670] bg-white"
    :class="{ 'opacity-50 grayscale': isOutOfStock }"
  >
    <!-- Image Container -->
    <div
      class="relative w-full aspect-square bg-[#e8ecf1] overflow-hidden flex-shrink-0"
    >
      <!-- Placeholder -->
      <div
        v-if="!item.image || imageError"
        class="w-full h-full bg-gradient-to-br from-[#e8ecf1] to-[#d8dfe7] flex items-center justify-center"
      >
        <!-- Subtle Grid Pattern -->
        <div class="absolute inset-0 opacity-[0.03]">
          <div class="absolute top-0 left-0 w-full h-full">
            <svg
              class="w-full h-full"
              viewBox="0 0 100 100"
              xmlns="http://www.w3.org/2000/svg"
            >
              <defs>
                <pattern
                  id="gridPattern"
                  x="0"
                  y="0"
                  width="10"
                  height="10"
                  patternUnits="userSpaceOnUse"
                >
                  <path
                    d="M 10 0 L 0 0 0 10"
                    fill="none"
                    stroke="#000"
                    stroke-width="0.5"
                  />
                </pattern>
              </defs>
              <rect
                width="100"
                height="100"
                fill="url(#gridPattern)"
              />
            </svg>
          </div>
        </div>

        <!-- Image Placeholder Icon - Universal Design -->
        <div class="relative z-10 text-center">
          <div class="relative inline-block">
            <!-- Main Icon Container -->
            <svg
              class="w-20 h-20 mx-auto text-neutral-300"
              viewBox="0 0 64 64"
              fill="none"
              stroke="currentColor"
            >
              <!-- Image frame -->
              <rect
                x="8"
                y="12"
                width="48"
                height="40"
                rx="4"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />

              <!-- Mountain/Landscape -->
              <path
                d="M8 42 L20 28 L32 38 L44 24 L56 36 L56 52 L8 52 Z"
                fill="currentColor"
                opacity="0.15"
                stroke="none"
              />
              <path
                d="M8 42 L20 28 L32 38 L44 24 L56 36"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />

              <!-- Sun/Circle -->
              <circle
                cx="44"
                cy="22"
                r="4"
                fill="currentColor"
                opacity="0.2"
              />
              <circle
                cx="44"
                cy="22"
                r="4"
                stroke-width="2"
                fill="none"
              />
            </svg>

            <!-- Decorative background circle -->
            <div
              class="absolute inset-0 -z-10 flex items-center justify-center"
            >
              <div class="w-24 h-24 rounded-full bg-neutral-200/30" />
            </div>
          </div>

          <!-- No Image Text -->
          <p
            class="text-xs font-semibold text-neutral-400 mt-3 tracking-wider uppercase"
          >
            No Image
          </p>
        </div>
      </div>

      <img
        v-else
        :src="item.image"
        class="w-full h-full object-cover"
        :class="{ grayscale: isOutOfStock }"
        @error="imageError = true"
      >

      <!-- Out of Stock Overlay -->
      <div
        v-if="isOutOfStock"
        class="absolute inset-0 flex items-center justify-center bg-black/60 z-20"
      >
        <div
          class="bg-red-600 text-white px-4 py-2 rounded-lg font-bold text-lg shadow-lg transform -rotate-12"
        >
          HABIS
        </div>
      </div>

      <!-- Best Seller Badge - Icon Only -->
      <div
        v-if="isBestSeller && !isOutOfStock"
        class="absolute top-2.5 right-2.5 z-20"
      >
        <img
          src="https://vamos-manggar-api.sejadikopi.com/storage/Logo/best-seller.png"
          alt="Best Seller"
          class="w-10 h-10 drop-shadow-lg hover:scale-110 transition-transform duration-200"
          draggable="false"
        >
      </div>
    </div>

    <!-- Menu Name Section with Smart Scrolling -->
    <div class="p-2 flex-grow text-left">
      <div
        ref="nameContainer"
        class="menu-name-container"
      >
        <p
          ref="menuName"
          class="text-sm font-medium leading-tight"
          :class="{
            'menu-name-scroll': isLongName,
            'menu-name-static': !isLongName,
          }"
          :style="scrollStyle"
        >
          {{ item.name }}
        </p>
        <!-- Fade gradient untuk efek meredup -->
        <div
          v-if="isLongName"
          class="fade-gradient"
        />
      </div>
      <p class="text-md font-bold mt-0.5 leading-tight">
        Rp {{ formatPrice(item.price) }}
      </p>

      <!-- Add Button -->
      <div
        v-if="!isOutOfStock"
        class="mt-1"
      >
        <button
          class="w-full text-white py-1.5 rounded-md text-sm flex items-center justify-center shadow transition-transform duration-300 ease-out"
          :class="[
            isAdded
              ? 'animate-pulse bg-[#5A6670] scale-105'
              : 'bg-[#2C3E50] hover:scale-105',
          ]"
          @click="handleAdd"
        >
          <span
            class="text-white font-bold text-base transition-all duration-300"
          >
            + Tambah
          </span>
        </button>
      </div>

      <!-- Button untuk Out of Stock -->
      <div
        v-else
        class="mt-1"
      >
        <button
          class="w-full py-1.5 rounded-md text-sm bg-gray-400 text-white cursor-not-allowed font-semibold"
          disabled
        >
          Stok Habis
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    item: {
      type: Object,
      default: () => ({}),
    },
    isOutOfStock: {
      type: Boolean,
      default: false,
    },
    isBestSeller: {
      type: Boolean,
      default: false,
    },
    // ⚠️ Props ini masih ada untuk backward compatibility, tapi tidak dipakai lagi
    activeVariantId: {
      type: Number,
      default: null,
    },
    setActiveVariant: {
      type: Function,
      default: () => {},
    },
    getMenuVariants: {
      type: Function,
      default: () => [],
    },
  },
  emits: ["add-to-cart"],
  data() {
    return {
      isAdded: false,
      isLongName: false,
      scrollDistance: 0,
      imageError: false,
    };
  },
  computed: {
    scrollStyle() {
      if (this.isLongName && this.scrollDistance > 0) {
        return {
          "--scroll-distance": `-${this.scrollDistance}px`,
        };
      }
      return {};
    },
  },
  mounted() {
    this.checkNameLength();
    window.addEventListener("resize", this.checkNameLength);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.checkNameLength);
  },
  methods: {
    formatPrice(val) {
      return val.toLocaleString("id-ID", {
        minimumFractionDigits: 0,
      });
    },
    checkNameLength() {
      this.$nextTick(() => {
        const nameElement = this.$refs.menuName;
        const containerElement = this.$refs.nameContainer;

        if (!nameElement || !containerElement) return;

        setTimeout(() => {
          const textWidth = nameElement.scrollWidth;
          const containerWidth = containerElement.clientWidth;

          if (textWidth > containerWidth) {
            this.isLongName = true;
            const overflowPixels = textWidth - containerWidth;
            this.scrollDistance = overflowPixels + 20;
          } else {
            this.isLongName = false;
            this.scrollDistance = 0;
          }
        }, 200);
      });
    },
    // ✅ SIMPLIFIED: Langsung emit ke parent, parent yang handle variant
    handleAdd() {
      if (this.isOutOfStock) return;

      // Emit item ke parent, parent akan cek apakah ada variant atau tidak
      this.$emit("add-to-cart", {
        id: this.item.id,
        name: this.item.name,
        price: this.item.price,
        image: this.item.image,
        description: this.item.description, // ⭐ TAMBAH DESCRIPTION
        kategori_id: this.item.categoryId,
        quantity: 1,
        note: "",
        additionals: this.item.additionals || [],
        variants: this.item.variants || [],
      });

      // Trigger animation
      this.isAdded = true;
      setTimeout(() => {
        this.isAdded = false;
      }, 500);
    },
  },
};
</script>

<style scoped>
/* Color Palette */
.bg-cream {
  background-color: #f5f7fa;
}

/* Menu Name Container */
.menu-name-container {
  width: 100%;
  overflow: hidden;
  position: relative;
  height: 1.1rem;
  display: flex;
  align-items: center;
}

.menu-name-static {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.menu-name-scroll {
  white-space: nowrap;
  display: inline-block;
  animation: smart-scroll 8s linear 2s infinite;
}

@keyframes smart-scroll {
  0% {
    transform: translateX(0);
  }
  20% {
    transform: translateX(0);
  }
  80% {
    transform: translateX(var(--scroll-distance, -50px));
  }
  100% {
    transform: translateX(var(--scroll-distance, -50px));
  }
}

.menu-name-container:hover .menu-name-scroll {
  animation-play-state: paused;
}
</style>
